import React, { useState } from 'react';
import {
  UploadCollection,
  FileUploader,
  Button,
  Title,
  UploadCollectionItem,
  Icon,
  Toast,
  Text,
  FlexBox,
  MessageBoxType,
  Toolbar,
  ToolbarSpacer,
  Ui5CustomEvent,
  UploadCollectionDomRef
} from '@ui5/webcomponents-react';
import { useStyles } from './Attachments.jss';
import { useTranslation } from 'react-i18next';
import '@ui5/webcomponents-icons/dist/attachment-photo';
import '@ui5/webcomponents-icons/dist/attachment-zip-file';
import '@ui5/webcomponents-icons/dist/pdf-attachment';
import { useFilesForAttachments } from '../../../hooks/useFilesForAttachments';
import { useGetDate } from 'hooks/useGetDate';
import { Status } from 'generated/graphql';
import { FINAL_STATUSES } from 'enum/Status';
import { ConfirmationDialog } from 'components/shared/ConfirmationDialog';
import { UploadCollectionItemDeleteEventDetail } from '@ui5/webcomponents-fiori/dist/UploadCollection';
import '@ui5/webcomponents-icons/dist/decline';

import ButtonDesign from '@ui5/webcomponents/dist/types/ButtonDesign.js';
import TitleLevel from '@ui5/webcomponents/dist/types/TitleLevel.js';
import UploadState from '@ui5/webcomponents-fiori/dist/types/UploadState.js';

export type AttachmentFile = {
  id: string;
  name: string;
  createdAt: string;
  uploadedBy: User;
  uploadState?: UploadState;
};

type User = {
  id?: string;
  firstName: string;
  lastName: string;
  displayName: string;
};

interface AttachmentsProps {
  queryId: string;
  queryStatus: Status;
  attachments: AttachmentFile[];
}

export const Attachments: React.FC<AttachmentsProps> = ({ queryId, queryStatus, attachments }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    handleFileUpload,
    handleFileDeleted,
    getFileIcon,
    handleDownload,
    handleDragAndDrop,
    toastMessage,
    toastOpen,
    setToastOpen,
    attachmentsList,
    EXTENSIONS_ICONS
  } = useFilesForAttachments({ queryId, attachments });
  const { getFormattedDateString } = useGetDate();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [deleteEvent, setDeleteEvent] = useState<Ui5CustomEvent<
    UploadCollectionDomRef,
    UploadCollectionItemDeleteEventDetail
  > | null>(null);

  const openConfirmationDialog = (
    event: Ui5CustomEvent<UploadCollectionDomRef, UploadCollectionItemDeleteEventDetail>
  ) => {
    setIsConfirmationDialogOpen(true);
    setDeleteEvent(event);
  };

  const onDeleteConfirmation = () => {
    if (deleteEvent) {
      handleFileDeleted(deleteEvent);
      setDeleteEvent(null);
    }
  };

  return (
    <div className={classes.div}>
      <Toolbar className={classes.toolbar}>
        <Title className={classes.toolbarTitle} level={TitleLevel.H4} size="H4" wrappingType="None">
          {t('display-query-title-attachments-uploaded', { count: attachmentsList.length })}
        </Title>
        <ToolbarSpacer />
        <FileUploader
          hideInput
          multiple
          onChange={handleFileUpload}
          accept={Object.keys(EXTENSIONS_ICONS).join(',')}
          data-testid="FileUploader"
          disabled={FINAL_STATUSES.includes(queryStatus)}
        >
          <Button design={ButtonDesign.Transparent} disabled={FINAL_STATUSES.includes(queryStatus)}>
            {t('display-query-attachments-upload-button')}
          </Button>
        </FileUploader>
      </Toolbar>
      <UploadCollection
        onItemDelete={openConfirmationDialog}
        onDrop={handleDragAndDrop}
        hideDragOverlay={FINAL_STATUSES.includes(queryStatus)}
        noDataDescription={FINAL_STATUSES.includes(queryStatus) ? ' ' : undefined}
        data-testid="UploadCollection"
      >
        {attachmentsList.map((attachment) => (
          <UploadCollectionItem
            id={attachment.id}
            key={attachment.id}
            fileName={attachment.name}
            thumbnail={<Icon name={getFileIcon(attachment.name)} />}
            fileNameClickable
            onFileNameClick={handleDownload}
            hideTerminateButton={true}
            uploadState={attachment.uploadState || UploadState.Complete}
            data-testid="UploadCollectionItemTest"
          >
            <FlexBox className={classes.metadata}>
              <Text>
                {t('display-query-attachments-uploaded-by', {
                  user: attachment.uploadedBy.displayName
                })}
              </Text>
              <Text>
                {t('display-query-attachments-uploaded-on', {
                  date: getFormattedDateString(attachment.createdAt)
                })}
              </Text>
            </FlexBox>
          </UploadCollectionItem>
        ))}
      </UploadCollection>
      <ConfirmationDialog
        data-testid="confirm-removing-attachments"
        open={isConfirmationDialogOpen}
        type={MessageBoxType.Warning}
        onClose={() => setIsConfirmationDialogOpen(false)}
        onConfirm={onDeleteConfirmation}
      >
        {t('confirm-removing-attachments')}
      </ConfirmationDialog>
      <Toast
        data-testid="attachments-toast"
        open={toastOpen}
        onClose={() => setToastOpen(false)}
        duration={5000}
      >
        {toastMessage}
      </Toast>
    </div>
  );
};
